import * as React from "react";
import {
  Dropdown,
  makeStyles,
  Option,
  shorthands,
  Tooltip,
  useId,
} from "@fluentui/react-components";
import type { DropdownProps, OptionProps } from "@fluentui/react-components";
import { FaCheck } from "react-icons/fa";
import { FC, useEffect, useState } from "react";
import { CommonInfo } from "../../CommonData/Config";

const useCustomOptionStyles = makeStyles({
  option: {
    display: "flex",
    // alignItems: "center",
    marginLeft: '-20px',
    ...shorthands.gap("5px"),
    paddingLeft: '6px',
    paddingRight: '6px'
  },
  icon: {
    ...shorthands.flex(0, 0, "auto"),
  },
  text: {
    ...shorthands.flex(1, 1, "auto"),
    lineHeight: '24px',
    fontSize: '12px',
    letterSpacing: '0px'
  },
});

type CustomOptionProps = Partial<OptionProps> & {
  icon?: string;
  text: string;
};

const CustomOption: FC<CustomOptionProps> = (props: CustomOptionProps) => {
  const { icon, text, ...optionProps } = props;
  const styles = useCustomOptionStyles();
  return (
    <Option
      text={text}
      className={styles.option}
      checkIcon={<FaCheck />}
      {...optionProps}
    >
      {icon && <img src={icon} style={{ width: "20px", height: '20px', borderRadius: '5px' }} className={styles.icon} />}
      <span className={styles.text}>{text}</span>
    </Option>
  );
};

const useStyles = makeStyles({
  root: {
    display: "grid",
    gridTemplateRows: "repeat(1fr)",
    justifyItems: "start",
    ...shorthands.gap("2px"),
    maxWidth: "100%",
    height: "37px",
    marginTop: "7px"
  },
  listbox: {
    maxHeight: "200px",
  },
  truncatedText: {
    overflowX: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: '100%',
    letterSpacing: '0%',
    fontSize: '13px',
    verticalAlign: 'center'
  },
  buttonContent: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
    marginLeft: '-10px'
  },
  icon: {
    width: "18px",
    height: "18px",
    flexShrink: 0,
  },
});

interface AnotherComponentProps {
  workplaceList: any[];
  value: string;
}

const getIcon = async (IconPath: string): Promise<string | undefined> => {
  const url = localStorage.getItem('tenantUrl') + IconPath;
  const isValid = await isValidImageUrl(url);
  return isValid ? url : undefined;
}

const isValidImageUrl = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);  // Image loaded successfully
    img.onerror = () => resolve(false);  // Error occurred while loading image
    img.src = url;
  });
};


export const CustomOptionsDropdown: FC<Partial<DropdownProps> & AnotherComponentProps> = ({
  workplaceList,
  value,
  ...props
}) => {
  const dropdownId = useId("dropdown");
  const styles = useStyles();
  const theme = localStorage.getItem('Theme')

  const [validIcons, setValidIcons] = useState<{ [key: string]: string | undefined }>({});

  useEffect(() => {
    const checkIcons = async () => {
      const icons = await Promise.all(
        workplaceList.map(async (option) => {
          const icon = await getIcon(option.IconPath);
          return { id: option.Id, icon };
        })
      );
      const validIconsMap = icons.reduce((acc, curr) => {
        acc[curr.id] = curr.icon;
        return acc;
      }, {} as { [key: string]: string | undefined });
      setValidIcons(validIconsMap);
    };

    checkIcons();
  }, [workplaceList]);

  const selectedWorkplace = React.useMemo(
    () => workplaceList.find((w) => w.Name === value),
    [workplaceList, value]
  );

  const iconUrl = selectedWorkplace
    ? validIcons[selectedWorkplace.Id]
    : undefined;

  return (
    <Dropdown
      aria-labelledby={dropdownId}
      className='dropdown'
      expandIcon={<img src={`${CommonInfo.folderName}${theme === 'dark' ? '/downarrowlight.svg' : '/downarrow.svg'}`} alt="arrow" style={{ width: 16, height: 16 }} />}
      button={
        <span className={styles.buttonContent}>
          {iconUrl && (
            <img
              src={iconUrl}
              alt=""
              className={styles.icon}
            />
          )}
          <Tooltip content={String(value)} relationship="label">
            <span
              className={styles.truncatedText}
              title={String(value)}
            >
              {value}
            </span>
          </Tooltip>
        </span>
      }
      {...props}
    >
      {workplaceList.map((option) => (
        <CustomOption key={option.Id} icon={validIcons[option.Id]} text={option.Name} />
      ))}
    </Dropdown>
  );
};
